/**
 * Timezone utilities for user timezone resolution and display formatting.
 * Uses JavaScript Intl API — no external libraries required.
 * IANA timezone names handle DST transitions automatically.
 */

import type { User } from '@/types/auth';

/**
 * Resolve the effective timezone for a user.
 * Chain: user.timezone > organization.timezone > 'UTC'
 */
export function resolveUserTimezone(user: Pick<User, 'timezone' | 'organization'> | null | undefined): string {
  if (!user) return 'UTC';
  return user.timezone || user.organization?.timezone || 'UTC';
}

/**
 * Format a UTC date for display in a specific timezone.
 */
export function formatInUserTimezone(
  utcDate: string | Date | null | undefined,
  timezone: string,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!utcDate) return 'Not specified';
  try {
    const d = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
    if (isNaN(d.getTime())) return 'Invalid date';

    const defaultOptions: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      ...options,
    };
    return new Intl.DateTimeFormat('en-US', defaultOptions).format(d);
  } catch {
    return 'Invalid date';
  }
}

/**
 * Format only the time portion for display in a specific timezone.
 */
export function formatTimeInTimezone(
  utcDate: string | Date | null | undefined,
  timezone: string
): string {
  if (!utcDate) return 'Not specified';
  try {
    const d = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
    if (isNaN(d.getTime())) return 'Invalid time';

    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(d);
  } catch {
    return 'Invalid time';
  }
}

/**
 * Format only the date portion for display in a specific timezone.
 */
export function formatDateInTimezone(
  utcDate: string | Date | null | undefined,
  timezone: string
): string {
  if (!utcDate) return 'Not specified';
  try {
    const d = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
    if (isNaN(d.getTime())) return 'Invalid date';

    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(d);
  } catch {
    return 'Invalid date';
  }
}

/** Common US timezone options for dropdowns */
export const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)', region: 'US' },
  { value: 'America/Chicago', label: 'Central Time (CT)', region: 'US' },
  { value: 'America/Denver', label: 'Mountain Time (MT)', region: 'US' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', region: 'US' },
  { value: 'America/Phoenix', label: 'Arizona (MST, no DST)', region: 'US' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)', region: 'US' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)', region: 'US' },
  { value: 'America/Puerto_Rico', label: 'Atlantic Time (AST)', region: 'US' },
  { value: 'UTC', label: 'UTC', region: 'Other' },
] as const;

/**
 * Get a display-friendly label for a timezone value.
 */
export function getTimezoneLabel(tz: string | null | undefined): string {
  if (!tz) return 'Not set';
  const option = TIMEZONE_OPTIONS.find(o => o.value === tz);
  return option?.label || tz;
}
